import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, CheckCircle2, Package, Truck } from 'lucide-react'
import { useCorrectOrderPartStatus, useOrder } from '@/api/hooks'
import { cn, formatMoney, formatPercent, formatStatus, parseUTCDate } from '@/lib/utils'
import { BackButton } from '@/components/back-button'
import { EntityActivityBadge } from '@/components/entity-activity-badge'
import { ShipmentDateBadges } from '@/components/shipment-dates'
import { purchasedShippingService } from '@/lib/purchased-shipping'
import {
  OrderLineActionResolution,
  ReopenOrderLineAction,
} from '@/components/order-line-action-resolution'
import { ManualStatusControl } from '@/components/manual-status-control'
import { ORDER_PART_STATUS_CHOICES } from '@/components/manual-status-options'
import { OrderProtection } from '@/features/safeware/order-protection'
import { usePageTitle } from '@/lib/page-title'
import { ACTION_FOCUS_CLASS, actionTargetProps, useActionFocus } from '@/hooks/use-action-focus'

interface OrderDetailPageProps {
  orderId: string
  actionUuid?: string
}

export function OrderDetailPage({ orderId, actionUuid }: OrderDetailPageProps) {
  const { data: order, isLoading } = useOrder(orderId)
  useActionFocus(actionUuid, !!order)
  usePageTitle(order && `Order ${order.name || order.order_number || `#${order.id}`}`)
  const correctPartStatus = useCorrectOrderPartStatus(orderId)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Order not found</p>
      </div>
    )
  }

  const sortedEvents = [...(order.events ?? [])].sort(
    (a, b) => parseUTCDate(b.shopify_created_at).getTime() - parseUTCDate(a.shopify_created_at).getTime(),
  )
  const purchasedShipping = purchasedShippingService(order.shopify_order?.shipping_lines_json)

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="space-y-1">
        <BackButton fallback={{ to: '/orders' }} fallbackLabel="Back to Orders" />

        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
          {order.project && (
            <>
              <Link
                to="/projects/$projectId"
                params={{ projectId: order.project.uuid }}
                className="truncate hover:text-foreground"
              >
                {order.project.name}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="font-medium text-foreground">
            {order.name || order.order_number || `#${order.id}`}
          </span>
        </nav>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Sales Order {order.name || order.order_number || `#${order.id}`}
          </h1>
          <div className="mt-1 flex items-center gap-2">
          <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
            {formatStatus(order.status)}
          </Badge>
          {order.financial_status && (
            <Badge variant="outline">{formatStatus(order.financial_status)}</Badge>
          )}
          {order.fulfillment_status && (
            <Badge variant="outline">{formatStatus(order.fulfillment_status)}</Badge>
          )}
          <EntityActivityBadge entityType="order" entityId={order.id} />
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Customer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {order.customer_group ? (
              <Link
                to="/customers/$groupId"
                params={{ groupId: order.customer_group.uuid }}
                className="font-medium text-primary hover:underline"
              >
                {order.customer_name || order.customer_group.name}
              </Link>
            ) : (
              <p className="font-medium">{order.customer_name || '-'}</p>
            )}
            {order.customer_email && <p className="text-muted-foreground">{order.customer_email}</p>}
            {order.customer_phone && <p className="text-muted-foreground">{order.customer_phone}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shipping</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {order.ship_to_name && <p className="font-medium">{order.ship_to_name}</p>}
            {order.ship_to_company && <p>{order.ship_to_company}</p>}
            {order.ship_to_address1 && <p>{order.ship_to_address1}</p>}
            {order.ship_to_address2 && <p>{order.ship_to_address2}</p>}
            {(order.ship_to_city || order.ship_to_state || order.ship_to_zip) && (
              <p>{[order.ship_to_city, order.ship_to_state, order.ship_to_zip].filter(Boolean).join(', ')}</p>
            )}
            {order.ship_to_country && <p>{order.ship_to_country}</p>}
            {purchasedShipping && (
              <div className="mt-3 border-t pt-3">
                <p className="text-xs text-muted-foreground">Purchased shipping</p>
                <p className="font-medium">{purchasedShipping}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-semibold">Line Items</h3>
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[52px]" />
                <TableHead>Product</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.line_items?.length ? (
                order.line_items.map((li) => (
                  <TableRow key={li.id}>
                    <TableCell className="pr-0">
                      {li.product?.image_url ? (
                        <img
                          src={li.product.image_url}
                          alt=""
                          className="h-10 w-10 rounded border object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                          <Package className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        {li.product?.uuid ? (
                          <Link
                            to="/products/$productId"
                            params={{ productId: li.product.uuid }}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {li.title}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium">{li.title}</span>
                        )}
                        {li.product?.manufacturer_name && (
                          <p className="text-xs text-muted-foreground">
                            {li.product.manufacturer_uuid ? (
                              <Link
                                to="/manufacturers/$manuId"
                                params={{ manuId: li.product.manufacturer_uuid }}
                                className="hover:underline hover:text-primary"
                              >
                                {li.product.manufacturer_name}
                              </Link>
                            ) : (
                              li.product.manufacturer_name
                            )}
                          </p>
                        )}
                        {li.sku && (
                          <p className="text-xs text-muted-foreground">SKU: {li.sku}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <ManualStatusControl
                          entityLabel="order part"
                          currentStatus={li.status}
                          statusRevision={li.status_revision}
                          statusSource={li.status_source}
                          statuses={ORDER_PART_STATUS_CHOICES}
                          loading={
                            correctPartStatus.isPending &&
                            correctPartStatus.variables?.lineItemId === li.id
                          }
                          onSubmit={(body, callbacks) =>
                            correctPartStatus.mutate({ lineItemId: li.id, body }, callbacks)
                          }
                          trigger={
                            <Badge variant="outline">{formatStatus(li.status)}</Badge>
                          }
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{li.quantity}</TableCell>
                    <TableCell className="text-right">{formatMoney(li.unit_price)}</TableCell>
                    <TableCell className="text-right">{formatMoney(li.line_total)}</TableCell>
                    <TableCell className="text-right">{formatMoney(li.cost_price)}</TableCell>
                    <TableCell className="text-right">{formatMoney(li.margin)}</TableCell>
                    <TableCell className="text-right">{formatPercent(li.margin_percent)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    No line items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 md:hidden">
          {order.line_items?.length ? (
            order.line_items.map((li) => (
              <div key={li.id} className="rounded-lg border bg-card p-3">
                <div className="flex items-start gap-3">
                  <div className="shrink-0">
                    {li.product?.image_url ? (
                      <img
                        src={li.product.image_url}
                        alt=""
                        className="h-10 w-10 rounded border object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {li.product?.uuid ? (
                      <Link
                        to="/products/$productId"
                        params={{ productId: li.product.uuid }}
                        className="block break-words text-sm font-medium text-primary hover:underline"
                      >
                        {li.title}
                      </Link>
                    ) : (
                      <span className="block break-words text-sm font-medium">{li.title}</span>
                    )}
                    {li.product?.manufacturer_name && (
                      <p className="text-xs text-muted-foreground">
                        {li.product.manufacturer_uuid ? (
                          <Link
                            to="/manufacturers/$manuId"
                            params={{ manuId: li.product.manufacturer_uuid }}
                            className="hover:underline hover:text-primary"
                          >
                            {li.product.manufacturer_name}
                          </Link>
                        ) : (
                          li.product.manufacturer_name
                        )}
                      </p>
                    )}
                    {li.sku && (
                      <p className="break-all text-xs text-muted-foreground">SKU: {li.sku}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <ManualStatusControl
                        entityLabel="order part"
                        currentStatus={li.status}
                        statusRevision={li.status_revision}
                        statusSource={li.status_source}
                        statuses={ORDER_PART_STATUS_CHOICES}
                        loading={
                          correctPartStatus.isPending &&
                          correctPartStatus.variables?.lineItemId === li.id
                        }
                        onSubmit={(body, callbacks) =>
                          correctPartStatus.mutate({ lineItemId: li.id, body }, callbacks)
                        }
                        trigger={
                          <Badge variant="outline">{formatStatus(li.status)}</Badge>
                        }
                      />
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-sm">
                    <p className="font-semibold tabular-nums">{formatMoney(li.line_total)}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {li.quantity} × {formatMoney(li.unit_price)}
                    </p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 border-t pt-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Net</p>
                    <p className="font-medium tabular-nums">{formatMoney(li.cost_price)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Profit</p>
                    <p className="font-medium tabular-nums">{formatMoney(li.margin)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Margin</p>
                    <p className="font-medium tabular-nums">{formatPercent(li.margin_percent)}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
              No line items
            </div>
          )}
        </div>
      </div>

      <OrderProtection orderUuid={orderId} />

      {(order.line_actions?.length ?? 0) > 0 && (
        <section className="space-y-3" aria-labelledby="line-actions-heading">
          <h3 id="line-actions-heading" className="text-base font-semibold">
            Manual purchasing
          </h3>
          <div className="space-y-2">
            {order.line_actions.map((action) => (
              <div
                key={action.id}
                {...actionTargetProps(action.uuid)}
                className={cn(
                  action.status === 'open'
                    ? 'rounded-lg border border-orange-200 bg-orange-50 p-3'
                    : 'rounded-lg border bg-card p-3',
                  ACTION_FOCUS_CLASS,
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      {action.status === 'open' ? (
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      )}
                      <p className="font-medium">{action.line_item.title}</p>
                      <Badge variant={action.status === 'open' ? 'secondary' : 'outline'}>
                        {formatStatus(action.status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{action.reason_details}</p>
                    <p className="text-xs text-muted-foreground">
                      Qty {action.line_item.quantity}
                      {action.line_item.sku ? ` · ${action.line_item.sku}` : ''}
                      {action.assigned_user ? ` · ${action.assigned_user.name}` : ''}
                    </p>
                    {action.disposition_notes && (
                      <p className="text-sm">{action.disposition_notes}</p>
                    )}
                    {action.manual_po_number && (
                      <p className="text-xs text-muted-foreground">Manual PO {action.manual_po_number}</p>
                    )}
                  </div>
                  {action.status === 'resolved' && action.disposition !== 'created_po' && (
                    <ReopenOrderLineAction action={action} orderUuid={orderId} />
                  )}
                  {action.status === 'resolved' &&
                    action.disposition === 'created_po' &&
                    action.purchase_order && (
                      <Link
                        to="/purchase-orders/$poId"
                        params={{ poId: action.purchase_order.uuid }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        View {action.purchase_order.po_number}
                      </Link>
                    )}
                </div>
                {action.status === 'open' && (
                  <div className="mt-3">
                    <OrderLineActionResolution action={action} orderUuid={orderId} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {order.total != null && (
        <div className="flex justify-end">
          <div className="space-y-1 text-right text-sm">
            {order.subtotal != null && (
              <p className="text-muted-foreground">Subtotal: {formatMoney(order.subtotal)}</p>
            )}
            {order.shipping_total != null && (
              <p className="text-muted-foreground">Shipping: {formatMoney(order.shipping_total)}</p>
            )}
            {order.tax_total != null && (
              <p className="text-muted-foreground">Tax: {formatMoney(order.tax_total)}</p>
            )}
            {order.discount_total != null && Number(order.discount_total) !== 0 && (
              <p className="text-muted-foreground">Discount: -{formatMoney(order.discount_total)}</p>
            )}
            <p className="text-lg font-bold">Total: {formatMoney(order.total)} {order.currency}</p>
            {Number(order.financials.refunded) > 0 && (
              <p className="font-medium text-destructive">
                Refunded: -{formatMoney(order.financials.refunded)}
              </p>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profitability</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">Revenue</p>
              <p className="text-lg font-semibold tabular-nums">{formatMoney(order.financials.revenue)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {order.financials.net_estimated ? 'Net (estimate)' : 'Net'}
              </p>
              <p className="text-lg font-semibold tabular-nums">{formatMoney(order.financials.net)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Profit</p>
              <p className="text-lg font-semibold tabular-nums">{formatMoney(order.financials.profit)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Margin</p>
              <p className="text-lg font-semibold tabular-nums">
                {formatPercent(order.financials.margin_percent)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {order.purchase_orders?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold">Purchase Orders</h3>
          <div className="space-y-2">
            {order.purchase_orders.map((po) => (
              <div key={po.id} className="rounded-md border">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <Link
                      to="/purchase-orders/$poId"
                      params={{ poId: po.uuid }}
                      className="font-medium text-primary hover:underline"
                    >
                      {po.po_number}
                    </Link>
                    {po.manufacturer && (
                      <Link
                        to="/manufacturers/$manuId"
                        params={{ manuId: po.manufacturer.uuid }}
                        className="text-sm text-muted-foreground hover:text-primary hover:underline"
                      >
                        {po.manufacturer.short_name}
                      </Link>
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {formatStatus(po.status)}
                    </Badge>
                  </div>
                  <span className="text-sm font-medium tabular-nums">
                    {formatMoney(po.total)}
                  </span>
                </div>
                {po.shipments?.length ? (
                  <div className="divide-y border-t">
                    {po.shipments.map((shipment) => {
                      const trackingRef = shipment.tracking_numbers || shipment.pro_number
                      return (
                        <div
                          key={shipment.id}
                          className="flex flex-wrap items-start justify-between gap-3 bg-muted/30 px-4 py-2"
                        >
                          <div className="flex min-w-0 items-start gap-2">
                            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <Link
                                  to="/shipments/$shipmentId"
                                  params={{ shipmentId: shipment.uuid }}
                                  className="text-sm font-medium text-primary hover:underline"
                                >
                                  {trackingRef ? (
                                    <span className="font-mono text-xs">{trackingRef}</span>
                                  ) : (
                                    'Shipment'
                                  )}
                                </Link>
                                <Badge
                                  variant={
                                    shipment.status === 'delivered'
                                      ? 'default'
                                      : shipment.status === 'shipped' ||
                                          shipment.status === 'in_transit'
                                        ? 'secondary'
                                        : 'outline'
                                  }
                                  className="text-[10px]"
                                >
                                  {formatStatus(shipment.status)}
                                </Badge>
                                {shipment.carrier_name && (
                                  <span className="text-xs text-muted-foreground">
                                    {shipment.carrier_name}
                                  </span>
                                )}
                              </div>
                              <ShipmentDateBadges
                                shipment={shipment}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedEvents.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold">Activity</h3>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {sortedEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-3 border-b pb-3 last:border-b-0 last:pb-0">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{event.action}</span>
                        {event.author && (
                          <span className="text-xs text-muted-foreground">by {event.author}</span>
                        )}
                      </div>
                      {event.message && (
                        <p className="mt-0.5 text-sm text-muted-foreground">{event.message}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {parseUTCDate(event.shopify_created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
